import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../components/datatable/DataTable";
import { useAuth } from "../contexts/AuthContext";
import API from "../api";
import toastr from "toastr";
import "./Billing.scss";

// ─── Types ────────────────────────────────────────────────────────────────────
interface Patient {
  id: number;
  P_code: string;
  P_name: string;
  prefix: string;
  mobile: string;
  ob_number?: string;
}

interface BillItem {
  item_id: number;
  item_name: string;
  item_amount: number;
}

interface Bill {
  id?: number;
  billnum: string;
  bill_date: string;
  patient_id: number | string;
  patient_name?: string;
  ref_prefix: string;
  reference: string;
  net_amount: number;
  payment_method: string;
  reference_no: string;
  bill_status: string;
  created_by: string;
  P_code?: string;
  P_name?: string;
  prefix?: string;
  mobile?: string;
}

interface Doctor {
  id: string;
  name: string;
}

// ─── Searchable Patient Select2 ───────────────────────────────────────────────
const PatientSelect2: React.FC<{
  value: string;
  onChange: (patientId: string, patient: Patient | null) => void;
}> = ({ value, onChange }) => {
  const [query, setQuery]                 = useState("");
  const [patients, setPatients]           = useState<Patient[]>([]);
  const [loading, setLoading]             = useState(false);
  const [open, setOpen]                   = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");
  const debounceRef                       = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef                      = useRef<HTMLDivElement>(null);
  const inputRef                          = useRef<HTMLInputElement>(null);

  // Fetch patients from backend (filtered by lab_id via auth token)
  const fetchPatients = useCallback((q: string) => {
    setLoading(true);
    API.get(`/patients/getPatients?search=${encodeURIComponent(q)}&pageSize=200`)
      .then((r) => setPatients(r.data.data || []))
      .catch(() => setPatients([]))
      .finally(() => setLoading(false));
  }, []);

  // Debounced search on query change
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPatients(query), 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, fetchPatients]);

  // Load initial list and reset when value is cleared externally
  useEffect(() => {
    if (!value) {
      setSelectedLabel("");
      setQuery("");
      fetchPatients("");
    }
  }, [value, fetchPatients]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (p: Patient) => {
    const label = `${p.P_code} – ${p.prefix || ""}${p.P_name}${p.mobile ? ` – ${p.mobile}` : ""}`;
    setSelectedLabel(label);
    setQuery("");
    setOpen(false);
    onChange(String(p.id), p);
  };

  const handleClear = () => {
    setSelectedLabel("");
    setQuery("");
    onChange("", null);
    fetchPatients("");
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // While the dropdown is open, show the live-typed query; otherwise show the selected label
  const displayValue = open ? query : selectedLabel;

  return (
    <div ref={containerRef} className="patient-select-container">
      {/* Search input styled as a form-control */}
      <div
        className="form-control d-flex align-items-center"
        style={{ cursor: "text", padding: "0 8px", minHeight: 38 }}
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}
      >
        <input
          ref={inputRef}
          type="text"
          className="patient-select-input"
          value={displayValue}
          placeholder={selectedLabel ? "" : "🔍  Search patient by name, code or mobile…"}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          style={{ border: "none", outline: "none", flex: 1, background: "transparent", fontSize: 14, minWidth: 0, color: "inherit" }}
        />
        {loading && (
          <span className="spinner-border spinner-border-sm text-secondary ms-1" style={{ flexShrink: 0 }} />
        )}
        {selectedLabel && !open && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleClear(); }}
            style={{ border: "none", background: "none", color: "inherit", opacity: 0.6, cursor: "pointer", padding: "0 2px", flexShrink: 0 }}
            title="Clear"
          >✕</button>
        )}
      </div>

      {/* Dropdown list */}
      {open && (
        <div className="patient-select-dropdown">
          {patients.length === 0 && !loading && (
            <div className="patient-select-empty">No patients found</div>
          )}
          {patients.map((p) => {
            const isSelected = String(p.id) === value;
            return (
              <div
                key={p.id}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(p); }}
                className={`patient-select-item ${isSelected ? "selected" : ""}`}
              >
                <span style={{ fontWeight: 600 }}>{p.P_code}</span>
                <span style={{ margin: "0 6px", opacity: 0.6 }}>–</span>
                <span>{p.prefix ? `${p.prefix} ` : ""}{p.P_name}</span>
                {p.mobile && <span className="patient-mobile" style={{ marginLeft: 8, fontSize: 12, opacity: 0.75 }}>{p.mobile}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Add Bill Modal ───────────────────────────────────────────────────────────
// onSuccess passes back the newly created bill_id so the caller can navigate
// to the receipt page right after saving.
const AddBillModal: React.FC<{
  show: boolean;
  onClose: () => void;
  onSuccess: (billId: number) => void;
  createdBy: string;
}> = ({ show, onClose, onSuccess, createdBy }) => {
  const emptyForm = {
    bill_num: "",
    bill_date: new Date().toISOString().slice(0, 10),
    patient_id: "",
    ref_prefix: "Self.",
    reference: "Self",
    description: "Consultation",
    amount: "",
    payment_method: "Cash",
    reference_no: "",
    pay_amount: "0.00",
  };

  const [form, setForm]                       = useState(emptyForm);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [doctors, setDoctors]                 = useState<Doctor[]>([{ id: "Self", name: "Self" }]);
  const [items, setItems]                     = useState<BillItem[]>([]);
  const [netAmount, setNetAmount]             = useState(0);
  const [balanceAmount, setBalanceAmount]     = useState(0);
  const [saving, setSaving]                   = useState(false);
  const itemCounter                           = useRef(0);

  useEffect(() => {
    if (!show) return;
    setForm(emptyForm);
    setSelectedPatient(null);
    setItems([]);
    setNetAmount(0);
    setBalanceAmount(0);
    itemCounter.current = 0;

    API.get("/billing/getBillNumber")
      .then((r) => setForm((f) => ({ ...f, bill_num: r.data.billnum })))
      .catch(() => toastr.error("Failed to fetch bill number"));

    API.get("/billing/getDoctors")
      .then((r) => {
        const data: Doctor[] = r.data.data || [];
        setDoctors(data.length ? data : [{ id: "Self", name: "Self" }]);
        setForm((f) => ({ ...f, reference: data[0]?.id || "Self" }));
      })
      .catch(() => setDoctors([{ id: "Self", name: "Self" }]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    setBalanceAmount(parseFloat(form.pay_amount || "0") - netAmount);
  }, [form.pay_amount, netAmount]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === "ref_prefix") {
      setForm((f) => ({ ...f, ref_prefix: value, reference: doctors[0]?.id || "Self" }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  };

  const addRow = () => {
    if (!form.amount || parseFloat(form.amount) <= 0) { toastr.warning("Please enter a valid amount"); return; }
    if (!["Consultation", "Pharmacy"].includes(form.description)) { toastr.warning("Select Consultation or Pharmacy"); return; }
    const newItem: BillItem = { item_id: itemCounter.current++, item_name: form.description, item_amount: parseFloat(form.amount) };
    const updated = [...items, newItem];
    setItems(updated);
    const newNet = updated.reduce((s, i) => s + i.item_amount, 0);
    setNetAmount(newNet);
    setForm((f) => ({ ...f, amount: "", pay_amount: f.pay_amount === "0.00" ? newNet.toFixed(2) : f.pay_amount }));
  };

  const removeRow = (item_id: number) => {
    const updated = items.filter((i) => i.item_id !== item_id);
    setItems(updated);
    setNetAmount(updated.reduce((s, i) => s + i.item_amount, 0));
  };

  const selectedPatientName = useMemo(() => {
    if (!selectedPatient) return "";
    return `${selectedPatient.prefix || ""} ${selectedPatient.P_name}`.trim();
  }, [selectedPatient]);

  const handleSave = async () => {
    if (!form.patient_id) { toastr.warning("Please select a patient"); return; }
    if (!items.length)    { toastr.warning("Add at least one item"); return; }
    if (netAmount <= 0)   { toastr.warning("Net total must be greater than zero"); return; }
    setSaving(true);
    try {
      const res = await API.post("/billing/saveBill", {
        bill_num: form.bill_num, bill_date: form.bill_date,
        patient_id: form.patient_id, patient_name: selectedPatientName,
        ref_prefix: form.ref_prefix, reference: form.reference,
        items: items.map((i) => ({
          item_id: i.item_id, item_name: i.item_name, item_type: i.item_name,
          item_amount: i.item_amount, quantity: 1, uom: "No.",
          unit_price: i.item_amount, gst: 0, test_category: "",
        })),
        total: netAmount, net_amount: netAmount, cgst: 0, sgst: 0, home_visit: 0,
        payment_method: form.payment_method, reference_no: form.reference_no,
        pay_amount: parseFloat(form.pay_amount), bill_status: "Paid", created_by: createdBy,
      });
      toastr.success("Bill saved successfully");
      onSuccess(res.data.bill_id);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Failed to save bill";
      toastr.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title"><i className="fa fa-file-invoice me-2" />New Invoice Bill</h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>
          <div className="modal-body">
            <div className="row mb-3">
              <div className="col-md-3">
                <label className="form-label fw-semibold">Bill Date</label>
                <input type="date" className="form-control" name="bill_date" value={form.bill_date} onChange={handleChange} />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Bill No.</label>
                <input type="text" className="form-control" value={form.bill_num} readOnly />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Referred By</label>
                <div className="input-group">
                  <select className="form-select" style={{ maxWidth: 110 }} name="ref_prefix" value={form.ref_prefix} onChange={handleChange}>
                    <option value="Self.">Self.</option>
                    <option value="Dr.">Dr.</option>
                  </select>
                  <select className="form-select" name="reference" value={form.reference} onChange={handleChange}>
                    {doctors.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* ── Patient: Select2-style live search filtered by lab_id ── */}
            <div className="row mb-3">
              <div className="col-md-8">
                <label className="form-label fw-semibold">Patient</label>
                <PatientSelect2
                  value={form.patient_id}
                  onChange={(patientId, patient) => {
                    setForm((f) => ({ ...f, patient_id: patientId }));
                    setSelectedPatient(patient);
                  }}
                />
              </div>
            </div>

            <div className="row align-items-end mb-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">Description</label>
                <select className="form-select" name="description" value={form.description} onChange={handleChange}>
                  <option value="Consultation">Consultation</option>
                  <option value="Pharmacy">Pharmacy</option>
                  <option value="Lab" disabled>Lab</option>
                  <option value="Radiology" disabled>Radiology</option>
                  <option value="Procedure" disabled>Procedure</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Amount (₹)</label>
                <input type="number" className="form-control text-end" name="amount" value={form.amount} onChange={handleChange} placeholder="0.00" min="0" />
              </div>
              <div className="col-md-2">
                <button type="button" className="btn btn-light w-100" onClick={addRow}>
                  <i className="fa fa-plus me-1" /> Add
                </button>
              </div>
            </div>

            <div className="table-responsive mb-3">
              <table className="table table-bordered table-striped">
                <thead style={{ backgroundColor: "#81888c", color: "white" }}>
                  <tr>
                    <th className="text-center" style={{ width: 50 }}>S.No</th>
                    <th className="text-center">Description</th>
                    <th className="text-center" style={{ width: 140 }}>Amount (₹)</th>
                    <th className="text-center" style={{ width: 80 }}>Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr><td colSpan={4} className="text-center text-muted py-3">No items added yet</td></tr>
                  ) : (
                    items.map((item, idx) => (
                      <tr key={item.item_id}>
                        <td className="text-center">{idx + 1}</td>
                        <td>{item.item_name}</td>
                        <td className="text-end">{item.item_amount.toFixed(2)}</td>
                        <td className="text-center">
                          <button className="btn btn-light btn-sm" onClick={() => removeRow(item.item_id)}>
                            <i className="fa fa-times" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <div className="row mb-2">
                  <div className="col-5 fw-semibold">Payment Method</div>
                  <div className="col-7">
                    <select className="form-select form-select-sm" name="payment_method" value={form.payment_method} onChange={handleChange}>
                      {["Cash","Credit Card","Debit Card","Online Payment","Cheque","Demand Draft"].map((m) => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
                <div className="row">
                  <div className="col-5 fw-semibold">Reference No.</div>
                  <div className="col-7">
                    <input type="text" className="form-control form-control-sm" name="reference_no" value={form.reference_no} onChange={handleChange} maxLength={100} />
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                {[
                  { label: "Net Total (₹)",  value: netAmount.toFixed(2),     readOnly: true,  name: "" },
                  { label: "Pay Amount (₹)", value: form.pay_amount,          readOnly: false, name: "pay_amount" },
                  { label: "Balance (₹)",    value: balanceAmount.toFixed(2), readOnly: true,  name: "" },
                ].map(({ label, value, readOnly, name }) => (
                  <div className="row mb-2 align-items-center" key={label}>
                    <div className="col-5 fw-semibold">{label}</div>
                    <div className="col-7">
                      <input type="text" className="form-control form-control-sm text-end" name={name} value={value} readOnly={readOnly} onChange={readOnly ? undefined : handleChange} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}><i className="fa fa-arrow-left me-1" /> Cancel</button>
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</> : <><i className="fa fa-check me-1" />Save Bill</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(value: unknown): string {
  if (!value) return "—";
  const d = new Date(value as string);
  return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString("en-IN");
}
function formatAmount(value: unknown): string {
  const n = parseFloat(value as string);
  return isNaN(n) ? "0.00" : n.toFixed(2);
}

// ─── Main Billing Page ────────────────────────────────────────────────────────
const Billing: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const today      = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 7) + "-01";

  const [startDate, setStartDate]       = useState(monthStart);
  const [endDate, setEndDate]           = useState(today);
  const [appliedDates, setAppliedDates] = useState({ startDate: monthStart, endDate: today });

  const [showAddModal, setShowAddModal] = useState(false);

  const tableRef = useRef<any>(null);
  const [searchContainer, setSearchContainer] = useState<HTMLDivElement | null>(null);
  const [visibleBills, setVisibleBills] = useState<Bill[]>([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const onVisibleDataChange = useCallback((rows: Bill[], loading: boolean) => {
    setVisibleBills(rows);
    setTableLoading(loading);
  }, []);

  const downloadReport = async (kind: "excel" | "pdf") => {
    if (tableLoading || exporting || !visibleBills.length) return;
    setExporting(true);
    try {
      const search = tableRef.current?.getSearchTerm() ?? "";
      const response = await API.get("/billing/getBills", {
        params: {
          exportAll: true,
          search,
          startdate: appliedDates.startDate,
          enddate: appliedDates.endDate,
        },
      });
      const allBills: Bill[] = response.data.data ?? [];
      if (!allBills.length) {
        toastr.info("No bills match the current filters");
        return;
      }
      const headers = ["ID", "Date", "Bill No.", "Patient ID", "Patient", "Amount (INR)"];
      // Sort only the report; keep the UI table in its existing order.
      const exportBills = [...allBills].sort((a, b) => {
        const first = a.bill_date ? new Date(a.bill_date).getTime() : NaN;
        const second = b.bill_date ? new Date(b.bill_date).getTime() : NaN;
        if (Number.isNaN(first)) return Number.isNaN(second) ? 0 : 1;
        if (Number.isNaN(second)) return -1;
        return first - second;
      });
      const rows = exportBills.map((bill) => [
        bill.id ?? "", formatDate(bill.bill_date), bill.billnum ?? "—",
        bill.P_code ?? "—", `${bill.prefix || ""}.${bill.P_name || ""}`.trim() || "—",
        Number(formatAmount(bill.net_amount)),
      ]);
      const total = rows.reduce((sum, row) => sum + Math.round(Number(row[5]) * 100), 0) / 100;
      const filename = `billing-${appliedDates.startDate}-${appliedDates.endDate}`;
      if (kind === "excel") {
        const { Workbook } = await import("exceljs");
        const workbook = new Workbook();
        const sheet = workbook.addWorksheet("Billing");
        sheet.addRow(headers);
        rows.forEach((row) => sheet.addRow(row));
        sheet.addRow(["", "", "", "", "Total Amount", total]);
        sheet.columns.forEach((column, index) => { column.width = [12, 16, 22, 20, 36, 20][index]; });
        sheet.getColumn(6).numFmt = '0.00';
        sheet.getRow(1).font = { bold: true };
        sheet.lastRow!.font = { bold: true };
        const buffer = await workbook.xlsx.writeBuffer();
        const url = URL.createObjectURL(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = `${filename}.xlsx`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } else {
        const { jsPDF } = await import("jspdf");
        const pdf = new jsPDF("landscape", "mm", "a4");
        const widths = [20, 32, 48, 42, 95, 40];
        const margin = 10;
        let y = 24;
        const drawRow = (values: (string | number)[], bold = false) => {
          pdf.setFont("helvetica", bold ? "bold" : "normal");
          const lines = values.map((value, index) => pdf.splitTextToSize(String(value), widths[index] - 4));
          const height = Math.max(...lines.map((line: string[]) => line.length)) * 5 + 4;
          if (y + height > 200) {
            pdf.addPage();
            y = 12;
            drawRow(headers, true);
            pdf.setFont("helvetica", bold ? "bold" : "normal");
          }
          let x = margin;
          lines.forEach((line: string[], index) => {
            pdf.rect(x, y, widths[index], height);
            const right = index === 5;
            pdf.text(line, right ? x + widths[index] - 2 : x + 2, y + 6, { align: right ? "right" : "left" });
            x += widths[index];
          });
          y += height;
        };
        pdf.setFontSize(14);
        pdf.text("Billing", margin, 12);
        pdf.setFontSize(10);
        pdf.text(`${appliedDates.startDate} to ${appliedDates.endDate}`, margin, 19);
        drawRow(headers, true);
        rows.forEach((row) => drawRow(row.map((value, index) => index === 5 ? Number(value).toFixed(2) : value)));
        drawRow(["", "", "", "", "Total Amount", total.toFixed(2)], true);
        pdf.save(`${filename}.pdf`);
      }
    } catch {
      toastr.error("Failed to download billing report");
    } finally {
      setExporting(false);
    }
  };

  const fetchBills = useCallback(
    async (page: number, search: string, sortBy = "id", sortOrder = "desc") => {
      try {
        const res = await API.get(
          `/billing/getBills?page=${page}&pageSize=25&search=${encodeURIComponent(search)}` +
          `&sortBy=${sortBy}&sortOrder=${sortOrder}` +
          `&startdate=${appliedDates.startDate}&enddate=${appliedDates.endDate}`
        );
        return { data: res.data.data ?? [], total: res.data.total ?? 0 };
      } catch {
        toastr.error("Failed to fetch bills");
        return { data: [], total: 0 };
      }
    },
    [appliedDates]
  );

  const handleSearch = () => {
    if (endDate < startDate) { toastr.warning("End date must be after start date"); return; }
    setAppliedDates({ startDate, endDate });
  };

  const handleDelete = async (id: number, billnum: string) => {
    if (!window.confirm(`Delete bill ${billnum}? This cannot be undone.`)) return;
    try {
      await API.delete(`/billing/deleteBill/${id}`);
      toastr.success("Bill deleted");
      tableRef.current?.refetch();
    } catch {
      toastr.error("Failed to delete bill");
    }
  };

  // ── FIXED: navigate() instead of window.open — receipts now open in the
  // SAME TAB using the billing-only routes (BillingBillPdf.tsx /
  // BillingPosReceipt.tsx), separate from the patient module's
  // /lab/BillPdf/:id and /lab/posreciept/:id. ──
  const goToA4Receipt  = (billId: number) => navigate(`/lab/billing/billpdf/${billId}`);
  const goToPosReceipt = (billId: number) => navigate(`/lab/billing/posreceipt/${billId}`);

  const columns = useMemo(
    () => [
      { header: "ID", accessorKey: "id" },
      {
        header: "Date", accessorKey: "bill_date",
        cell: ({ row }: any) => formatDate(row.original.bill_date),
      },
      {
        header: "Bill No.", accessorKey: "billnum",
        cell: ({ row }: any) => row.original.billnum ?? "—",
      },
      {
        header: "Patient ID", accessorKey: "P_code",
        cell: ({ row }: any) => row.original.P_code ?? "—",
      },
      {
        header: "Patient", id: "patient_name",
        cell: ({ row }: any) =>
          `${row.original.prefix || ""}.${row.original.P_name || ""}`.trim() || "—",
      },
      {
        header: "Amount (₹)", accessorKey: "net_amount",
        cell: ({ row }: any) => formatAmount(row.original.net_amount),
      },
      {
        header: "Actions", id: "actions",
        cell: ({ row }: any) => {
          const bill: Bill = row.original;
          return (
            <div className="d-flex justify-content-center gap-1">
              <button className="btn btn-light" title="View A4 Receipt" onClick={() => goToA4Receipt(bill.id!)}>
                <i className="fa fa-file-alt" />
              </button>
              <button className="btn btn-light" title="View POS Receipt" onClick={() => goToPosReceipt(bill.id!)}>
                <i className="fa fa-print" />
              </button>
              <button className="btn btn-light" title="Delete" onClick={() => handleDelete(bill.id!, bill.billnum)}>
                <i className="fa fa-trash" />
              </button>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appliedDates]
  );

  return (
    <div className="billing-page">
      <header className="billing-header">
        <h1>Invoice Bill</h1>
        <div className="billing-header-search" ref={setSearchContainer} />
        <div className="billing-header-actions">
          <div className="billing-date-filter">
            <label htmlFor="billing-from-date">From Date</label>
            <input id="billing-from-date" type="date" className="form-control billing-header-control" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="billing-date-filter">
            <label htmlFor="billing-to-date">To Date</label>
            <input id="billing-to-date" type="date" className="form-control billing-header-control" value={endDate} max={today} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <button type="button" className="btn btn-primary billing-header-control" onClick={handleSearch} aria-label="Apply date filters" title="Apply date filters">
            <i className="fa fa-search" aria-hidden="true" />
          </button>
          <button type="button" className="btn btn-light px-4 billing-header-control" onClick={() => setShowAddModal(true)}>
            <i className="fa fa-plus me-1" /> New Entry
          </button>
        </div>
      </header>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-outline card-primary">
            <div className="card-body">
              <DataTable
                ref={tableRef}
                searchContainer={searchContainer}
                onVisibleDataChange={onVisibleDataChange}
                columns={columns}
                queryFn={fetchBills}
                queryKey={["bills", appliedDates]}
              />
              <div className="d-flex flex-wrap gap-2 mt-3">
                <button type="button" className="btn btn-success" disabled={tableLoading || exporting || !visibleBills.length} onClick={() => downloadReport("excel")}>
                  Download Excel
                </button>
                <button type="button" className="btn btn-danger" disabled={tableLoading || exporting || !visibleBills.length} onClick={() => downloadReport("pdf")}>
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FIXED: on save, close modal, refresh table, and navigate() to the
          A4 receipt in the SAME TAB — matching the old "generate and view
          right away" behavior, but using the BillPdf design/size. ── */}
      <AddBillModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={(billId) => {
          setShowAddModal(false);
          tableRef.current?.refetch();
          navigate(`/lab/billing/billpdf/${billId}`);
        }}
        createdBy={user?.full_name || ""}
      />
    </div>
  );
};

export default Billing;